import { Fragment, useEffect, useState } from 'react';

import { useLDFlag } from '@/data/LD/selectors/getLDFlag';
import useDialog from '@/dialogs/hooks/useDialog';
import logo from '@/rippling/assets/img/rippling-logo.svg';
import { RipplingCTALink } from '@/rippling/components/RipplingCTALink';
import RipplingDemoOverlay from '@/rippling/components/RipplingDemoOverlay';
import { RIPPLING_PHONE_COLLECTION_DIALOG } from '@/rippling/dialogs';
import { useRipplingDemo } from '@/rippling/hooks/useRipplingDemo';
import useRipplingMigration from '@/rippling/hooks/useRipplingMigration';
import type { DemoModalAction } from '@/rippling/types/BannerConfig';
import { isValidNavCrossSellConfig, type RipplingNavCrossSellConfig } from '@/rippling/types/NavCrossSellConfig';
import FontIcon from '@/shared/ui/FontIcon';
import { Popover } from '@/shared/ui/Popover';
import { posthogCapture } from '@/shared/vendor/posthog';

import type { IconName } from '@wiw/icons';

import '@/rippling/assets/styles/RipplingNavCrossSell.scss';

interface RipplingNavCrossSellProps {
  isDesktop: boolean;
}

const isDirectUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://');
};

const RipplingNavCrossSell = ({ isDesktop }: RipplingNavCrossSellProps) => {
  const flagValue = useLDFlag('nav-xs-configuration');
  const [opened, setOpened] = useState(false);

  const { open: openDialog } = useDialog();
  const {
    isDemoOpen,
    needsPhoneCollection,
    open: openDemo,
    closeDemo,
    setPendingBookingData,
    submitPendingBooking,
  } = useRipplingDemo();
  const { migrationActive } = useRipplingMigration();

  useEffect(() => {
    if (needsPhoneCollection) {
      openDialog(RIPPLING_PHONE_COLLECTION_DIALOG, {
        onComplete: submitPendingBooking,
        showCloseButton: true,
      });
    }
  }, [needsPhoneCollection, openDialog, submitPendingBooking]);

  if (migrationActive || !flagValue || !isValidNavCrossSellConfig(flagValue)) {
    return null;
  }

  const config = flagValue as RipplingNavCrossSellConfig;
  const ContainingElement = isDesktop ? 'li' : 'div';
  const buttonIcon = (config.buttonIcon || 'payroll') as IconName;

  const handleToggle = () => {
    setOpened(prev => {
      const willOpen = !prev;
      if (willOpen && config.phOpenEvent) {
        posthogCapture(config.phOpenEvent);
      }
      return willOpen;
    });
  };

  const handleCTAClick = () => {
    setOpened(false);
    if (config.phCtaEvent) {
      posthogCapture(config.phCtaEvent);
    }
  };

  const openDemoModal = (actionConfig: DemoModalAction) => {
    handleCTAClick();
    const requirePhone = actionConfig.requirePhoneCollection ?? false;
    openDemo({ requirePhoneCollection: requirePhone });
  };

  const renderCTA = (): React.ReactNode => {
    const action = config.ctaAction;

    if (action.type === 'demo-modal') {
      return (
        <button type="button" className="btn btn-md btn-primary-gold" onClick={() => openDemoModal(action)}>
          {config.ctaText}
        </button>
      );
    }

    if (action.type === 'link') {
      const url = action.url;

      if (isDirectUrl(url)) {
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-md btn-primary-gold"
            onClick={handleCTAClick}
          >
            {config.ctaText}
          </a>
        );
      }

      const campaign = action.utmParams?.utm_campaign || url;
      const source = action.utmParams?.utm_source || 'wiw-app';
      const medium = action.utmParams?.utm_medium || 'link';

      return (
        <RipplingCTALink
          campaign={campaign}
          source={source}
          medium={medium}
          className="btn btn-md btn-primary-gold"
          onClick={handleCTAClick}
        >
          {config.ctaText}
        </RipplingCTALink>
      );
    }

    return null;
  };

  const demoModalAction = config.ctaAction?.type === 'demo-modal' ? config.ctaAction : null;

  return (
    <Fragment>
      <ContainingElement className="rippling-nav-cross-sell">
        <Popover
          width={isDesktop ? 400 : 358}
          position={isDesktop ? 'bottom-end' : 'bottom'}
          shadow="md"
          opened={opened}
          onChange={setOpened}
          offset={{ mainAxis: 11, crossAxis: 10 }}
          arrowOffset={15}
        >
          <Popover.Target>
            <button type="button" className="nav-button" onClick={handleToggle}>
              <FontIcon icon={buttonIcon} />
              <span className="nav-item-label">{config.buttonLabel}</span>
              {!opened && <FontIcon icon="chevron-down" />}
              {opened && <FontIcon icon="chevron-up" />}
            </button>
          </Popover.Target>
          <Popover.Dropdown className="rippling-nav-cross-sell-dropdown">
            <div className="preferred-partner-label">{config.badgeText}</div>
            <h3>
              <img src={logo} alt="Rippling Payroll" />
            </h3>
            <p>{config.body}</p>
            {renderCTA()}
          </Popover.Dropdown>
        </Popover>
      </ContainingElement>
      <RipplingDemoOverlay
        isOpen={isDemoOpen}
        onClose={closeDemo}
        queueNameOverride={demoModalAction?.queueNameOverride}
        leadRoutingOverride={demoModalAction?.leadRoutingOverride}
        utmParams={demoModalAction?.utmParams}
        requirePhoneCollection={demoModalAction?.requirePhoneCollection}
        onBookingNeedsPhone={setPendingBookingData}
      />
    </Fragment>
  );
};

export default RipplingNavCrossSell;
